import { useCallback, useEffect, useRef, useState } from "react";
import { fetchMoodboards } from "@/screens/creative-library/apiService";
import type { Moodboard } from "@/screens/creative-library/types";

type UseMoodboardsManagerArgs = {
  accountId?: string | null;
};

type MoodboardsUpdater = (prev: Moodboard[]) => Moodboard[];

export function useMoodboardsManager({ accountId }: UseMoodboardsManagerArgs) {
  const [moodboards, setMoodboards] = useState<Moodboard[]>([]);
  const [moodboardsLoading, setMoodboardsLoading] = useState(false);
  const [selectedMoodboardId, setSelectedMoodboardId] = useState<string | null>(null);
  const [moodboardDialogOpen, setMoodboardDialogOpen] = useState(false);
  const [moodboardDialogInitialActiveId, setMoodboardDialogInitialActiveId] = useState<
    string | null
  >(null);

  const moodboardsAbortRef = useRef<AbortController | null>(null);

  const reloadMoodboards = useCallback(async () => {
    if (!accountId) return;
    moodboardsAbortRef.current?.abort();
    const controller = new AbortController();
    moodboardsAbortRef.current = controller;
    setMoodboardsLoading(true);
    try {
      const res = await fetchMoodboards(accountId, { signal: controller.signal });
      setMoodboards(res.data || []);
    } catch (error) {
      if (controller.signal.aborted) return;
      // keep error handling at call site if needed; default to console
      console.error("Failed to fetch moodboards:", error);
    } finally {
      if (!controller.signal.aborted) setMoodboardsLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    void reloadMoodboards();
    return () => {
      moodboardsAbortRef.current?.abort();
    };
  }, [reloadMoodboards]);

  /**
   * Opens the moodboard manager dialog WITHOUT changing the currently selected "Style".
   * Use this for managing/editing moodboards so the user's selection doesn't jump.
   */
  const openMoodboardDialog = useCallback((activeMoodboardId?: string) => {
    if (activeMoodboardId) setMoodboardDialogInitialActiveId(activeMoodboardId);
    else setMoodboardDialogInitialActiveId(null);
    setMoodboardDialogOpen(true);
  }, []);

  const onMoodboardsChange = useCallback((updater: MoodboardsUpdater) => {
    setMoodboards((prev) => updater(prev));
  }, []);

  return {
    moodboards,
    moodboardsLoading,
    selectedMoodboardId,
    setSelectedMoodboardId,
    moodboardDialogOpen,
    setMoodboardDialogOpen,
    moodboardDialogInitialActiveId,
    openMoodboardDialog,
    reloadMoodboards,
    onMoodboardsChange,
  };
}


